import {
  ensureTableElementSettings,
  TABLE_ROW_MIN_HEIGHT,
} from "@library-elements/table";
import { nanoid } from "@reduxjs/toolkit";
import type { PickerVariable } from "../variable-picker/types";

export type TVariableElementTemplate = {
  id: string;
  element: string;
  category: string;
  order: number;
  previewImage: null;
  json: {
    id: string;
    type: "element";
    children: never[];
    settings: Record<string, unknown>;
  };
};

// Mirrors elements-library get-empty-text-element.ts settings shape, EXCEPT
// horizontalAlignment: get-empty-text-element.ts / entity-builders.ts use
// "center" only for slide TITLES (see titleTextElement); every plain body/
// placeholder text element (placeholderTextElement) uses "start" (= Left,
// per alignment-dropdown-horizontal.tsx's HORIZONTAL_OPTIONS). A variable
// dropped as a new element is body content, not a title, so it should match
// the "start" default like any other normal new text element.
export const buildTextElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Paragraph",
  category: "Text",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    settings: {
      content: `<p>${variable.token}</p>`,
      horizontalAlignment: "start",
      name: "RichText",
      placeholder: "<p>Click to add text</p>",
    },
  },
});

// Mirrors media-lib make-images-panel-image-element.ts settings shape; the
// variable binds via dynamicVariableSrc, src stays empty → placeholder + badge.
export const buildImageElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Image",
  category: "Images",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    settings: {
      src: "",
      dynamicVariableSrc: variable.token,
      alt: variable.name,
      shadow: "none",
      round: "8px",
      width: "75",
      selectedLinkType: "none",
      horizontalAlignment: "center",
      shape: "free",
      overlayOpacity: "80",
      metadata: { scale: 1, translateX: 50, translateY: 50 },
      name: "Image",
    },
  },
});

// Mirrors media-lib make-videos-panel-video-element.ts settings shape; the
// variable binds via dynamicVariableSrc, src stays empty → placeholder + badge.
export const buildVideoElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Video",
  category: "Video",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    settings: {
      src: "",
      dynamicVariableSrc: variable.token,
      type: "Library",
      shadow: "none",
      round: "8px",
      width: "75",
      controls: true,
      loop: true,
      autoplay: true,
      horizontalAlignment: "center",
      shape: "16-9",
      view: "inline",
      metadata: {},
      name: "Video",
    },
  },
});

// Mirrors elements-library get-empty-embedded-element settings shape (see
// EmbeddedProps in @library-elements/embedded/types + MAPPED_CATEGORY_ICONS'
// "Embedded tools" category). `active: "link"` picks the URL/iframe mode
// (vs "code"); EmbeddedPlaceholder shows the built-in "Add your HTML here"
// empty state while link is unset. dynamicVariableSrc mirrors the
// Image/Video binding convention (see buildImageElementForVariable) — note
// parse-version-variables.ts's elementDynamicVariable() switch does not yet
// have an "Embedded" case, so runtime token substitution for this field is
// NOT wired; this only fixes what element TYPE gets created on insert.
export const buildEmbeddedElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Embedded",
  category: "Embedded tools",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    settings: {
      link: "",
      dynamicVariableSrc: variable.token,
      code: "",
      active: "link",
      // Inline, not the link-preview card: an iframe variable is meant to show
      // the embedded page/video itself once the version fills the URL.
      view: "inline",
      width: 100,
      height: 300,
      round: "0",
      isShadow: false,
      horizontalAlignment: "center",
      name: "Embedded",
    },
  },
});

// Mirrors makeIconPickerIconElement's settings shape (icon-picker/utils/
// make-icon-picker-icon-element.ts) — the same 5-field minimal shape shared
// by every other Icon-creation site in the codebase (entity-builders.ts'
// placeholderIconElement/iconElement use the same fields). Unlike Image/
// Video, an empty `icon` renders as a genuinely invisible element (Icon.
// library.tsx renders `settings.icon` directly as a class/ligature string,
// with no placeholder-box fallback), so — unlike those two — this uses a
// visible neutral glyph rather than an empty string; the variable still
// binds via dynamicVariableSrc and parse-version-variables.ts resolves it
// onto `icon` at publish time, replacing this placeholder. "ri-star-line" is
// the same neutral Remix-icon-line glyph entity-builders.ts's
// PLACEHOLDER_ICON_PALETTE uses as ITS first/default placeholder icon.
const ICON_PLACEHOLDER_GLYPH = "ri-star-line";

export const buildIconElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Icon",
  category: "Icons",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    settings: {
      icon: ICON_PLACEHOLDER_GLYPH,
      dynamicVariableSrc: variable.token,
      fontSize: "48",
      color: "var(--theme-color-4)",
      horizontalAlignment: "center",
      name: "Icon",
    },
  },
});

/** Starter grid for a data-variable table: square, empty, and obviously a table. */
const DATA_TABLE_SIZE = 3;

// A "data" variable carries a 2D matrix for a WHOLE table, so the binding lives
// on the Table element (`dataDynamicVariable`) rather than as a token inside one
// cell — the token-in-a-cell shape this used to build made the variable render
// as a single stringified cell instead of populating the table. The cells here
// are only an authoring placeholder: when the variable resolves, the matrix
// replaces the table's cells/rows/columns outright. `dataDynamicVariable` keeps
// the `{{token}}` form so the deck scan still collects the variable into the
// fs-vars manifest (and the share dialog still renders a row for it).
//
// NOTE: distinct from `listDynamicVariable`, which clones one template row per
// record substituting `[Key]` placeholders (that's the list/repeatable path).
export const buildDataElementForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => ({
  id: nanoid(),
  element: "Table",
  category: "Tables",
  order: 0,
  previewImage: null,
  json: {
    id: nanoid(),
    type: "element",
    children: [],
    // Built through the canonical defaults so this table is identical to one
    // added from the elements library apart from the overrides below. Hand-
    // copying the settings list previously drifted from it (it omitted
    // `height`, which collapsed the element's resize outline).
    settings: ensureTableElementSettings({
      dataDynamicVariable: variable.token,
      tableRows: DATA_TABLE_SIZE,
      tableCols: DATA_TABLE_SIZE,
      height: DATA_TABLE_SIZE * TABLE_ROW_MIN_HEIGHT,
      width: "100",
      tableColWidths: Array.from(
        { length: DATA_TABLE_SIZE },
        () => 100 / DATA_TABLE_SIZE,
      ),
      // Empty starter grid: the cells are pure placeholder, so they must not
      // carry the variable's name or token as text — either would survive as
      // real cell text whenever the variable has no data yet.
      tableData: Array.from(
        { length: DATA_TABLE_SIZE * DATA_TABLE_SIZE },
        () => ({ id: nanoid(), colSpan: 1, rowSpan: 1, content: "" }),
      ),
    }),
  },
});

export const buildElementTemplateForVariable = (
  variable: PickerVariable,
): TVariableElementTemplate => {
  if (variable.type === "video") return buildVideoElementForVariable(variable);
  if (variable.type === "image" || variable.type === "media")
    return buildImageElementForVariable(variable);
  if (variable.type === "iframe")
    return buildEmbeddedElementForVariable(variable);
  if (variable.type === "data") return buildDataElementForVariable(variable);
  if (variable.type === "icon") return buildIconElementForVariable(variable);
  return buildTextElementForVariable(variable);
};
